import logger from "@utils/logger";
import { useEffect } from "react";

/**
 * Classifies an error and returns a professional, user-facing message.
 * Technical details are kept out of the UI and logged to console only.
 */
export function getContextualErrorMessage(error: unknown): string {
  const message =
    error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();

  // Network and connectivity errors
  if (
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("network") ||
    message.includes("err_internet_disconnected") ||
    message.includes("net::") ||
    message.includes("cors")
  ) {
    return "Unable to connect to the guidelines service. Please check your internet connection and try again.";
  }

  // Sanity CMS / content delivery errors
  if (
    message.includes("sanity") ||
    message.includes("groq") ||
    message.includes("content lake") ||
    message.includes("cdn.sanity.io") ||
    message.includes("apicdn.sanity.io")
  ) {
    return "Clinical guidelines content is temporarily unavailable. Please try again shortly.";
  }

  // Timeout errors
  if (
    message.includes("timeout") ||
    message.includes("timed out") ||
    message.includes("aborterror") ||
    message.includes("aborted")
  ) {
    return "The request took too long. Please try again.";
  }

  return "An unexpected error occurred. Please refresh the page and try again.";
}

const useGlobalErrorHandler = (
  setErrorMessage: React.Dispatch<React.SetStateAction<string | null>>
) => {
  useEffect(() => {
    const handleGlobalError = (event: ErrorEvent) => {
      logger.error("Global error caught:", event.error);
      setErrorMessage(getContextualErrorMessage(event.error));
      event.preventDefault();
    };

    window.addEventListener("error", handleGlobalError);

    return () => {
      window.removeEventListener("error", handleGlobalError);
    };
  }, [setErrorMessage]);
};

export default useGlobalErrorHandler;